import { DEFAULT_CLUB_TIMEZONE, isValidTimezone } from '@jupi/shared'

// Display formatting, in the club's timezone.
//
// The legacy pages rendered dates as d.m.Y H:i and relied on PHP's
// date_default_timezone_set('Europe/Helsinki'); the API now returns ISO
// instants, so the conversion happens here instead. Everything a member
// reads is club wall-clock time regardless of where their phone thinks it
// is -- a booking at 14:00 is at 14:00 at the airfield.
//
// WHICH zone that is is a runtime setting (intra.settings `club.timezone`,
// served by GET /api/time). ServerClockProvider calls setClubTimezone when
// it syncs; until then these format in the default, which is what the
// database is seeded with. The provider re-renders its subtree on the
// change, so the switch reaches every mounted formatter.

export { DEFAULT_CLUB_TIMEZONE } from '@jupi/shared'

let clubTimezone: string = DEFAULT_CLUB_TIMEZONE

export const getClubTimezone = () => clubTimezone

// Returns true when the zone actually changed, so the caller can decide
// whether a re-render is warranted. Ignores a name Intl will not accept --
// a bad settings row must not break every date on the page.
export function setClubTimezone(timeZone: string | null | undefined): boolean {
  if (!timeZone || timeZone === clubTimezone || !isValidTimezone(timeZone)) return false
  clubTimezone = timeZone
  cache.clear()
  return true
}

// Intl.DateTimeFormat construction is the expensive part, so each shape is
// built once per zone and kept. The cache is cleared, not keyed, on a zone
// change: the old zone is never coming back within a session.
const cache = new Map<string, Intl.DateTimeFormat>()

const OPTIONS: Record<string, Intl.DateTimeFormatOptions> = {
  dateTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  dateOnly: { day: '2-digit', month: '2-digit', year: 'numeric' },
  timeOnly: { hour: '2-digit', minute: '2-digit' },
  timeWithSeconds: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
  dayAndTime: { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' },
}

function formatter(
  shape: keyof typeof OPTIONS | string,
  timeZone = clubTimezone,
  // fi-FI writes a time as 14.30, which is what every booking on the page
  // reads as. The header clock is the exception: a UTC reading beside it
  // is aviation's, and aviation writes 14:30.
  locale = 'fi-FI',
): Intl.DateTimeFormat {
  const key = `${shape}|${timeZone}|${locale}`
  let found = cache.get(key)
  if (!found) {
    found = new Intl.DateTimeFormat(locale, {
      timeZone,
      hourCycle: 'h23',
      ...OPTIONS[shape],
    })
    cache.set(key, found)
  }
  return found
}

const toDate = (value: string | Date | number) => (value instanceof Date ? value : new Date(value))

export const formatDateTime = (value: string | Date) => formatter('dateTime').format(toDate(value))
export const formatDate = (value: string | Date) => formatter('dateOnly').format(toDate(value))
export const formatTime = (value: string | Date) => formatter('timeOnly').format(toDate(value))

// The header clock's two readings: the same instant in UTC and in the
// club's zone. `timeZone` is explicit so the UTC line does not depend on
// what the club setting happens to be.
export const formatClockTime = (
  value: string | Date | number,
  timeZone: string,
  showSeconds = false,
) => formatter(showSeconds ? 'timeWithSeconds' : 'timeOnly', timeZone, 'en-GB').format(toDate(value))

// "UTC", "UTC+2", "UTC+3" -- aviation convention, where Intl says "GMT+3".
export function formatUtcOffset(value: string | Date | number, timeZone: string): string {
  const part = new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'shortOffset' })
    .formatToParts(toDate(value))
    .find((p) => p.type === 'timeZoneName')
  // Intl renders the zero offset as "GMT+0" (a bare "GMT" on some ICU
  // builds), which would read as "UTC+0"; the zero offset is written "UTC"
  // everywhere else in the UI.
  return part?.value.replace('GMT', 'UTC').replace(/^UTC[+-]0$/, 'UTC') ?? 'UTC'
}

// The city half of an IANA name -- "Europe/Helsinki" -> "Helsinki" -- for
// the caption under the clock. Underscores are separators in IANA names.
export const timezoneLabel = (timeZone: string) =>
  (timeZone.split('/').pop() ?? timeZone).replaceAll('_', ' ')

// "10:00–14:00" when both ends fall on one day, "15.09 10:00 – 17.09 14:00"
// when they do not. Multi-day bookings are common enough (cross-country
// trips) that collapsing them to times alone would mislead.
export function formatRange(start: string | Date, end: string | Date): string {
  const from = toDate(start)
  const to = toDate(end)
  const sameDay = formatter('dateOnly').format(from) === formatter('dateOnly').format(to)
  return sameDay
    ? `${formatter('timeOnly').format(from)}–${formatter('timeOnly').format(to)}`
    : `${formatter('dayAndTime').format(from)} – ${formatter('dayAndTime').format(to)}`
}

// Flight hours, with the decimal comma Finnish members expect.
export function formatHours(hours: number | string | null | undefined): string {
  if (hours == null) return '–'
  const value = typeof hours === 'string' ? Number(hours) : hours
  if (!Number.isFinite(value)) return '–'
  return value.toFixed(2).replace('.', ',')
}
